import { create } from 'zustand';
import axios from 'axios';
import { getSecureItem, setSecureItem, removeSecureItem } from '../lib/secureStorage.js';
import { useAdminStore } from './useAdminStore.js';

interface User {
  id: string;
  username: string;
  email: string;
  avatar_url: string | null;
  banner_url?: string | null;
  bio: string | null;
  accent_color?: string | null;
  total_xp: number;
  rank_title: string;
  is_admin?: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isInitialized: boolean;
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  login: (accessToken: string, refreshToken: string, user: User) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  initialize: () => Promise<void>;
}

const API_URL = import.meta.env.VITE_API_URL ?? '';

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  isInitialized: false,

  setUser: (user) => set({ user }),
  setAccessToken: (token) => set({ accessToken: token }),

  login: async (accessToken, refreshToken, user) => {
    set({ accessToken, user });
    await setSecureItem('refreshToken', refreshToken);

    // Ensure current user's equipped cosmetic is applied to the stored user.avatar_url
    try {
      const res = await axios.get(`${API_URL}/api/cosmetics/user/${user.username}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      const equipped = res.data?.equippedAvatar;
      if (equipped?.imageUrl) {
        set({ user: { ...user, avatar_url: equipped.imageUrl } });
      }
    } catch (err) {
      // non-fatal
    }

    // Send monitoring app launch event if first boot or track session
    try {
      await axios.post(
        `${API_URL}/api/events`,
        { event_name: 'app_opened' },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
    } catch (err) {
      console.warn('Failed to send app_opened event on login:', err);
    }
  },

  logout: async () => {
    const token = get().accessToken;
    set({ accessToken: null, user: null });
    useAdminStore.getState().clearSession();
    await removeSecureItem('refreshToken');

    try {
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
    } catch (err) {
      console.warn('Logout API request failed:', err);
    }
  },

  refreshAccessToken: async () => {
    try {
      const storedRefreshToken = await getSecureItem('refreshToken');

      // Attempt to hit the refresh endpoint
      const response = await axios.post(
        `${API_URL}/api/auth/refresh`,
        { refresh_token: storedRefreshToken },
        {
          withCredentials: true,
          headers: storedRefreshToken ? { Authorization: `Bearer ${storedRefreshToken}` } : {}
        }
      );

      const { access_token, refresh_token, user } = response.data;
      set({ accessToken: access_token, user });
      await setSecureItem('refreshToken', refresh_token);

      // Fetch equipped cosmetic for the current user and override avatar_url if present
      try {
        const res = await axios.get(`${API_URL}/api/cosmetics/user/${user.username}`, {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        const equipped = res.data?.equippedAvatar;
        if (equipped?.imageUrl) {
          set({ user: { ...user, avatar_url: equipped.imageUrl } });
        }
      } catch (err) {
        // ignore; keep server-provided avatar_url
      }
      return true;
    } catch (err) {
      // Clear credentials if token refresh failed
      set({ accessToken: null, user: null });
      await removeSecureItem('refreshToken');
      return false;
    }
  },

  initialize: async () => {
    if (get().isInitialized) return;
    const success = await get().refreshAccessToken();

    if (success && get().accessToken) {
      // Send silent app launch event
      try {
        await axios.post(
          `${API_URL}/api/events`,
          { event_name: 'app_opened' },
          { headers: { Authorization: `Bearer ${get().accessToken}` } }
        );
      } catch (err) {
        console.warn('Failed to log app_opened on initialization:', err);
      }
    } else {
      // Unauthenticated app launch log
      try {
        await axios.post(`${API_URL}/api/events`, { event_name: 'app_opened' });
      } catch (err) {
        console.warn('Failed to log app_opened on init:', err);
      }
    }

    set({ isInitialized: true });
  }
}));
